import { useContext, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import PropTypes from 'prop-types'
import { Grid } from '@aura/components'
import { withStyles } from '@aura/components/styles'
import { useCookies } from 'react-cookie'
import { useTranslation } from 'react-i18next'
import { useAuraThemeSwitcher } from '@aura/components/themes'
import AppBarNew from '../../../../components/app-bar-tool'
import AuthStatus from '../../../../constants/auth-status'
import InitStatus from '../../../../constants/init-status'
import appContext from '../../../../utils/app-context'
import MFA from '../mfa-main'
import CustomLoader from '../../../../components/custom-loader'
import SessionTimeout from '../../../../components/session-timeout'
import { cookieNameLoginSourceUrl, cookieNameSessionTimeout, cookieNameLoginAuth, cookieNameAppAutoRedirect, cookieRedirectedOnceToDM } from '../../../../constants/app'
import { SESSION_TIMEOUT } from '../../../../constants/routes'
import loginLayoutType from '../../../../constants/login-layout'
import MaintenanceBanner from '../../../../components/maintenance-banner'
import { useConfig } from '../../../../utils/config'
import { sanitizeRedirectUrl } from '../../../../utils/url-validation'
import DefaultLogin from './default-login'
import styles from './styles'


const ALLOWED_DOMAINS = [ 'rbccm.com', 'rbc.com', 'sterbccm.com', 'fg.rbc.com', 'devfg.rbc.com', 'saifg.rbc.com' ]


const Component = ({
    authData,
    authVerified,
    authenticatorData,
    authenticatorStatus,
    authStatus,
    bannerInfo,
    dataQRCode,
    error,
    errorAuth,
    errorForgotPwd,
    errorSession,
    forgotPwdProgress,
    forgotPwdSuccessMsg,
    init,
    initAuthenticators,
    initEnrollAuthFactor,
    initPush,
    initBanner,
    closeBanner,
    onActivateAuthAuthenticatorCode,
    onActivateAuthenticatorCode,
    onAuthenticate,
    onBackToAuthenticators,
    onBackToOptionalAuthenticators,
    onBackFallbackEnforceMFA,
    onBackFallbackEnforceMFATwoSteps,
    onEnroll,
    onFinish,
    onForgotPassword,
    onQRCodeCannotScan,
    onQRCodeCannotScanNext,
    onQRCodeNext,
    onResendAuthChallenge,
    onResendChallenge,
    onSendChallenge,
    onSetAuthenticator,
    onSetDeviceType,
    onSetOptionalAuthenticator,
    onSkipEnrollment,
    onVerifyAccountRecovery,
    onVerifyAuthAccountRecovery,
    onVerifyAuthenticatorCode,
    qrCodeStatus,
    resetAuthVerified,
    resendSuccess,
    resendSuccessMsg,
    stepNumber,
    initStatus,
    isVerified,
    mfaRequired,
    sessionTimeout,
    getAccountRecovery,
    accountRecoveryData,
    accountRecoveryStatus,
    onEnrollInit,
    validatePhoneAtLogin,
    OnValidatePhoneAtLogin,
    onRequestLoginMagicLink,
    magicLinkLoginMsg,
    errorMagicLinkLogin
}) => {
    const history = useHistory()
    const config = useConfig()
    const [ cookies, setCookie, removeCookie ] = useCookies([ cookieNameLoginAuth, cookieNameLoginSourceUrl, cookieNameSessionTimeout, cookieNameAppAutoRedirect ])
    const { theme, updateTheme } = useAuraThemeSwitcher()
    const { application: applicationContext } = useContext(appContext)

    const mainDomain = useMemo(() => window.location.hostname.substring(window.location.hostname.indexOf('.')), [])

    const [ redirecting, setRedirecting ] = useState(false)
    const [ showLoader, setShowLoader ] = useState(true)

    const { t: translate } = useTranslation()
    
    const shouldRedirect = useMemo(() =>
        authData && authData.appGAURL && redirecting,
    [ authData?.appGAURL, redirecting ]
    )

    useEffect(() => {
        if (!authData) {
            init(true, config?.oktaCurrentSession, config?.logoutOAGSession, config?.cmxClean, config?.secure2Clean, config?.protectedClean, config?.digitalMarketplace)
        }
        removeCookie(cookieNameLoginAuth, { domain: mainDomain, path: '/' })
    }, [ init, removeCookie ])

    useEffect(() => {
        if (authData && authData.appGAURL && !cookies[cookieNameLoginSourceUrl]) {
            setCookie(cookieNameLoginSourceUrl, window.location.href)
        }
    }, [ authData?.appGAURL ])

    useEffect(() => {
        updateTheme({ palette: 'light' })
    }, [ ])

    useEffect(() => {
        if (cookies[cookieNameLoginSourceUrl]) {
            removeCookie(cookieNameLoginSourceUrl)
            const safeUrl = sanitizeRedirectUrl(cookies[cookieNameLoginSourceUrl], '/login')
            if (window.location.href !== safeUrl) {
                window.location = safeUrl
            }
        }
    }, [ cookies[cookieNameLoginSourceUrl] ])

    useEffect(() => {
        if (cookies[cookieNameSessionTimeout]) {
            removeCookie(cookieNameSessionTimeout, { path: '/' })
            history.push(SESSION_TIMEOUT)
        }
    }, [])

    useEffect(() => {
        if (authData && authData.appGAURL && !redirecting) {
            setRedirecting(true)
            sessionStorage.removeItem(cookieRedirectedOnceToDM)
            setCookie(cookieNameAppAutoRedirect, 'true', { domain: mainDomain, path: '/', sameSite: 'strict' })
            window.location.replace(sanitizeRedirectUrl(authData.appGAURL, '/login', ALLOWED_DOMAINS))
        }
    }, [ authData, redirecting ])

    useEffect(() => {
        initBanner()
    }, [])

    useEffect(() => {
        if (initStatus === InitStatus.SUCCESS && !window.__autoRedirecting) {
            setShowLoader(false)
        } else {
            setShowLoader(true)
        }
    }, [ initStatus ])
    
    useEffect(() => {
        if (!showLoader) {
            document.body.classList.remove('loader-active')
            const staticLoader = document.getElementById('static-loader')
            if (staticLoader) {
                staticLoader.style.display = 'none'
            }
        }
    }, [ showLoader ])

    if (sessionTimeout) {
        return <SessionTimeout />
    }
    
    if (shouldRedirect) {
        return (
            <CustomLoader
                label={translate('loading')}
                style={{ background: '#FFFFFF', height: '100vh', width: 'auto' }}
            />
        )
    }

    if (authData && !authData.appGAURL) {
        return (<>
            <AppBarNew />
            <MFA {...{
                accountRecoveryData,
                accountRecoveryStatus,
                appGAURL: authData ? authData.appGAURL : '',
                authData,
                authenticatorData,
                authenticatorStatus,
                authVerified,
                dataQRCode,
                error,
                errorSession,
                getAccountRecovery,
                initAuthenticators,
                initEnrollAuthFactor,
                initPush,
                isProcessing: authStatus === AuthStatus.PENDING,
                isVerified,
                mfaRequired,
                onActivateAuthAuthenticatorCode,
                onActivateAuthenticatorCode,
                onBackFallbackEnforceMFA,
                onBackFallbackEnforceMFATwoSteps,
                onBackToAuthenticators,
                onBackToOptionalAuthenticators,
                onEnroll,
                onEnrollInit,
                onFinish,
                onQRCodeCannotScan,
                onQRCodeCannotScanNext,
                onQRCodeNext,
                onResendAuthChallenge,
                onResendChallenge,
                onSendChallenge,
                onSetAuthenticator,
                onSetDeviceType,
                onSetOptionalAuthenticator,
                onSkipEnrollment,
                OnValidatePhoneAtLogin,
                onVerifyAccountRecovery,
                onVerifyAuthAccountRecovery,
                onVerifyAuthenticatorCode,
                qrCodeStatus,
                resendSuccess,
                resendSuccessMsg,
                resetAuthVerified,
                stepNumber,
                validatePhoneAtLogin
            }} />
        </>)
    }

    return (<>
        <MaintenanceBanner bannerInfo={bannerInfo} closeBanner={closeBanner} />
        <div style={bannerInfo.display ? styles.bannerOpen : {}} />
        <Grid container sx={applicationContext?.loginLayout === loginLayoutType.CENTER ? styles.centralLoginBox : styles.loginContainerGrid} spacing={0}>
            {!redirecting && <DefaultLogin {...{
                applicationContext,
                authStatus,
                errorAuth,
                errorForgotPwd,
                errorMagicLinkLogin,
                forgotPwdProgress,
                forgotPwdSuccessMsg,
                magicLinkLoginMsg,
                onAuthenticate,
                onForgotPassword,
                onRequestLoginMagicLink
            }} />}
        </Grid>
    </>)
}

Component.displayName = 'login'

Component.defaultProps = {
    stepNumber: 0
}

Component.propTypes = {
    authData: PropTypes.object,
    authenticatorData: PropTypes.string,
    authenticatorStatus: PropTypes.string,
    authStatus: PropTypes.string,
    error: PropTypes.object,
    errorAuth: PropTypes.object,
    errorSession: PropTypes.string,
    initAuthenticators: PropTypes.func.isRequired,
    initEnrollAuthFactor: PropTypes.func.isRequired,
    initPush: PropTypes.func.isRequired,
    onActivateAuthAuthenticatorCode: PropTypes.func.isRequired,
    onActivateAuthenticatorCode: PropTypes.func.isRequired,
    onBackToAuthenticators: PropTypes.func.isRequired,
    onBackToOptionalAuthenticators: PropTypes.func.isRequired,
    onEnroll: PropTypes.func.isRequired,
    onFinish: PropTypes.func.isRequired,
    onQRCodeCannotScan: PropTypes.func.isRequired,
    onQRCodeCannotScanNext: PropTypes.func.isRequired,
    onQRCodeNext: PropTypes.func.isRequired,
    onResendAuthChallenge: PropTypes.func.isRequired,
    onResendChallenge: PropTypes.func.isRequired,
    onSetAuthenticator: PropTypes.func.isRequired,
    onSetDeviceType: PropTypes.func.isRequired,
    onSetOptionalAuthenticator: PropTypes.func,
    onSkipEnrollment: PropTypes.func.isRequired,
    onVerifyAccountRecovery: PropTypes.func.isRequired,
    onVerifyAuthAccountRecovery: PropTypes.func.isRequired,
    onVerifyAuthenticatorCode: PropTypes.func.isRequired,
    resendSuccess: PropTypes.bool,
    resendSuccessMsg: PropTypes.string,
    stepNumber: PropTypes.number
}

export default withStyles(Component, styles)
